<script setup lang="ts">
// Signal-style onboarding: open link → pick a name → you're in. Keys are generated on
// this device; there is no password.
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ApiError, describeError } from '../api/http'
import type { InvitePreview } from '../api/types'
import { isValidUsername, suggestUsername } from '../lib/format'
import { decryptName, isEncrypted, keyFromInvite } from '../lib/metacrypto'
import { useSession } from '../stores/session'
import AuthFrame from '../components/AuthFrame.vue'
import { isApp, pageUrl, parseServerInput, setServer } from '../platform'

const route = useRoute()
const router = useRouter()
const session = useSession()

const code = computed(() => String(route.params.code ?? ''))
const preview = ref<InvitePreview | null>(null)
const previewError = ref<string | null>(null)
const displayName = ref('')
const username = ref('')
const usernameTouched = ref(false)
const showUsername = ref(false)
const busy = ref(false)
const error = ref<string | null>(null)
const elsewhere = ref(false)
const homeServer = ref('')

function joinFromHome(): void {
  const domain = homeServer.value.trim().replace(/^https?:\/\//, '').replace(/\/.*$/, '')
  if (!domain) return
  // Passed in the fragment so neither server ever sees the space key.
  const join = `/join#invite=${encodeURIComponent(pageUrl())}`
  if (isApp) {
    // The app talks to one server at a time: switch to the home one and join from there.
    setServer(parseServerInput(domain)?.origin ?? `https://${domain}`)
    window.location.replace(join)
    return
  }
  window.location.href = `${window.location.protocol}//${domain}${join}`
}

watch(displayName, (n) => {
  if (!usernameTouched.value) username.value = suggestUsername(n)
})

const isBootstrap = computed(() => preview.value && !preview.value.space_name)
const canSubmit = computed(
  () => displayName.value.trim().length > 0 && isValidUsername(username.value) && !busy.value,
)

onMounted(async () => {
  try {
    const p = await session.api.invitePreview(code.value)
    // The server only has the space name encrypted; the key is in this link's #fragment.
    if (isEncrypted(p.space_name)) {
      p.space_name = (await decryptName(keyFromInvite(pageUrl()) ?? undefined, 'space', p.space_name!)) ?? 'a private space'
    }
    preview.value = p
  } catch (e) {
    previewError.value = describeError(e)
  }
})

/** The full link (with its #k= key) so joining stores the space's name key. */
const fullInvite = () => pageUrl()

async function join(): Promise<void> {
  busy.value = true
  error.value = null
  try {
    const space = await session.register(fullInvite(), username.value, displayName.value.trim())
    await router.replace(session.spaceLanding(space))
  } catch (e) {
    error.value = describeError(e)
    if (e instanceof ApiError && e.is('username_taken')) showUsername.value = true
  } finally {
    busy.value = false
  }
}

async function acceptAsExisting(): Promise<void> {
  busy.value = true
  error.value = null
  try {
    const space = await session.acceptInvite(fullInvite())
    await router.replace(session.spaceLanding(space))
  } catch (e) {
    error.value = describeError(e)
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <AuthFrame testid="welcome" caption="invitation">

      <div v-if="previewError" data-testid="invite-error">
        <div class="mono mb-4 text-signal">ERR · INVITE</div>
        <h1 class="mb-2 text-2xl font-light">Invite unavailable</h1>
        <p class="text-sm text-text-muted">{{ previewError }}</p>
      </div>

      <div v-else-if="!preview" class="mono text-text-muted">Checking your invite…</div>

      <template v-else>
        <div class="mb-8">
          <p v-if="isBootstrap" class="text-sm text-text-muted">Welcome to your new hrth</p>
          <p v-else class="text-sm text-text-muted">
            <span class="text-text-primary">{{ preview.inviter.display_name }}</span>
            invited you to join
          </p>
          <h1 class="mt-2 text-3xl font-light tracking-tight" data-testid="invite-space">
            {{ preview.space_name ?? preview.instance_name }}
          </h1>
          <p v-if="preview.space_member_count" class="mono mt-2 text-text-muted">
            {{ preview.space_member_count }} {{ preview.space_member_count === 1 ? 'member' : 'members' }} ·
            {{ preview.domain }}
          </p>
          <p v-if="isBootstrap" class="mt-2 text-xs text-text-muted">
            You’ll be the admin. You can invite friends once you’re in.
          </p>
        </div>

        <!-- Already signed in on this device: just join the space. -->
        <div v-if="session.status === 'ready'" class="space-y-3">
          <button class="btn-primary w-full" :disabled="busy" data-testid="accept-invite" @click="acceptAsExisting">
            {{ busy ? 'Joining…' : `Join ${preview.space_name ?? 'hrth'}` }}
          </button>
          <button class="btn-ghost w-full" @click="router.push('/')">Not now</button>
        </div>

        <form v-else class="space-y-4" @submit.prevent="join">
          <div>
            <label class="label" for="display-name">What should friends call you?</label>
            <input
              id="display-name"
              v-model="displayName"
              class="input"
              maxlength="64"
              placeholder="Your name"
              autocomplete="nickname"
              autofocus
              data-testid="display-name"
            />
          </div>
          <div v-if="showUsername || displayName">
            <button
              v-if="!showUsername"
              type="button"
              class="text-left text-xs text-text-muted hover:text-text-secondary"
              @click="showUsername = true"
            >
              Your ID will be <span class="mono text-text-primary">{{ username }}@{{ preview.domain }}</span> ·
              change
            </button>
            <template v-else>
              <label class="label" for="username">Username</label>
              <div class="flex items-center gap-1">
                <input
                  id="username"
                  v-model="username"
                  class="input font-mono"
                  maxlength="32"
                  data-testid="username"
                  @input="usernameTouched = true"
                />
                <span class="mono shrink-0 text-text-muted">@{{ preview.domain }}</span>
              </div>
              <p v-if="username && !isValidUsername(username)" class="mt-1 text-xs text-error">
                2–32 characters: lowercase letters, numbers, . _ -
              </p>
            </template>
          </div>
          <p v-if="error" class="border border-signal px-3 py-2 text-sm text-error" data-testid="join-error">
            {{ error }}
          </p>
          <button class="btn-primary w-full" :disabled="!canSubmit" data-testid="join">
            {{ busy ? 'Setting up your keys…' : 'Join' }}
          </button>
          <p class="text-xs text-text-muted">
            No password needed. Your encryption keys are created on this device and never leave it.
          </p>
        </form>
        <div v-if="session.status !== 'ready' && !isBootstrap" class="mt-6 border-t border-border pt-4 text-xs text-text-muted">
          <button v-if="!elsewhere" class="hover:text-text-secondary" data-testid="join-elsewhere" @click="elsewhere = true">
            Already on hrth with another server? Join from there
          </button>
          <form v-else class="flex gap-2" @submit.prevent="joinFromHome">
            <input v-model="homeServer" class="input" placeholder="your-server.example" data-testid="home-server" />
            <button class="btn-primary shrink-0" :disabled="!homeServer.trim()" data-testid="join-from-home">Go</button>
          </form>
        </div>
      </template>
  </AuthFrame>
</template>
